import { ProductDetailResponse, ReferralProduct } from "../../../Redux/RefProduct/types";

export interface OtherShoppingVoucherItem {
  productId: string;
  productName: string;
  voucherAmount: number;
  voucherValidityDays: number;
  productPrice: number;
  productImage: string;
}

export interface RefProductDetailData {
  id: string;
  productId: string;
  ssin: string;
  title: string;
  description: string;
  image: string;
  images: string[];
  price: number;
  mrp: number;
  discountPercent: number;
  savingsAmount: number;
  rating: number;
  reviews: string;
  reviewCount: number;
  deal?: string;
  voucherBadge?: string;
  breadcrumb: string[];
  category: string;
  superCategory: string;
  subCategory?: string;
  brand: string;
  sellerName: string;
  sellerSlug?: string;
  sellerId?: string;
  stockStatus: string;
  availableQty: number;
  freeDelivery: boolean;
  fastDelivery: boolean;
  nextDayDelivery: boolean;
  shipsFrom: string;
  packaging: string;
  payment: string;
  giftOptions: string;
  country: string;
  condition: string;
  returnPolicy: string;
  warranty: string;
  colors?: { name: string; hex?: string; image?: string }[];
  sizes?: string[];
  bulletPoints: string[];
  attributes: Record<string, string>;
  voucherAmount?: number;
  voucherValidityDays?: number;
  autoRepurchaseAmount?: number;
  autoRepurchaseValidityDays?: number;
  referralPlanName?: string;
  region: string;
  tags: string[];
  otherShoppingVouchers: OtherShoppingVoucherItem[];
}

export const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(value)));

export function mapReferralToProductDetail(
  res: ProductDetailResponse,
): RefProductDetailData {
  const p: ReferralProduct = res.product;
  const rawImages =
    p.images && p.images.length > 0
      ? p.images
      : ["/assets/images/logos/salarlogo.png"];

  const unitPrice = Number(p.priceDetails?.unitPrice ?? 0);
  const finalPrice = Number(p.priceDetails?.finalPrice ?? unitPrice);
  const mrp = unitPrice > 0 ? unitPrice : finalPrice;
  const discountAmount = Number(
    p.priceDetails?.discountAmount ?? (mrp > finalPrice ? mrp - finalPrice : 0),
  );
  const discountPercent =
    mrp > 0 && mrp > finalPrice
      ? Math.round(((mrp - finalPrice) / mrp) * 100)
      : 0;

  const voucherAmount = p.shoppingVoucher?.amount;
  const voucherBadge = voucherAmount
    ? `₹${formatPrice(voucherAmount)} Voucher`
    : undefined;

  const deal =
    p.tags?.[0] ||
    voucherBadge ||
    (discountPercent > 0 ? `${discountPercent}% OFF` : undefined);

  const superCat = p.categoryDetails?.superCategory || "Referral";
  const cat = p.categoryDetails?.category || "Products";
  const subCat = p.categoryDetails?.subCategory;

  const breadcrumb = [
    "Home",
    "Referral Products",
    superCat !== "Referral" ? superCat : null,
    cat !== "Products" ? cat : null,
    subCat,
    p.productName,
  ].filter(Boolean) as string[];

  const bulletPoints =
    p.aboutThisItem && p.aboutThisItem.length > 0
      ? p.aboutThisItem
      : p.productDescription
      ? [p.productDescription]
      : [];

  const attributes: Record<string, string> = {
    Brand: p.brandName || "Salar Store",
    "Generic Name": cat,
    SSIN: p.ssin || "-",
    "Product ID": p.productId || p._id || "-",
    Region: p.region || "India",
  };

  if (p.referralPlanName) {
    attributes["Referral Plan"] = p.referralPlanName;
  }
  if (p.shoppingVoucher?.validityDays) {
    attributes["Voucher Validity"] = `${p.shoppingVoucher.validityDays} Days`;
  }
  if (p.shoppingVoucher?.autoRepurchaseAmount) {
    attributes["Auto Repurchase"] = `₹${formatPrice(
      p.shoppingVoucher.autoRepurchaseAmount,
    )}`;
  }

  return {
    id: String(p.productId || p._id),
    productId: String(p.productId || p._id),
    ssin: p.ssin || "",
    title: p.productName || "Referral Product",
    description: p.productDescription || "",
    image: rawImages[0],
    images: rawImages,
    price: finalPrice,
    mrp,
    discountPercent,
    savingsAmount: discountAmount,
    rating: Number((p as any).reviews?.averageRating || (p as any).rating || 0),
    reviews: (p as any).reviews?.totalReviews ? `${(p as any).reviews.totalReviews} ratings` : "",
    reviewCount: Number((p as any).reviews?.totalReviews || 0),
    deal,
    voucherBadge,
    breadcrumb,
    category: cat,
    superCategory: superCat,
    subCategory: subCat,
    brand: p.brandName || "Salar Store",
    sellerName: p.brandName || "Salar Store",
    sellerSlug: p.brandName?.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    sellerId: p.brandName,
    stockStatus: p.status === "inactive" ? "out_of_stock" : "in_stock",
    availableQty: 10,
    freeDelivery: true,
    fastDelivery: true,
    nextDayDelivery: false,
    shipsFrom: p.region || "Salar Fulfillment",
    packaging: "Discreet Eco Packaging",
    payment: "Secure Online Transaction",
    giftOptions: voucherAmount ? "Shopping Voucher Included" : "Standard",
    country: "India",
    condition: "New",
    returnPolicy: "-",
    warranty: "Manufacturer / Salar Warranty",
    bulletPoints,
    attributes,
    voucherAmount,
    voucherValidityDays: p.shoppingVoucher?.validityDays,
    autoRepurchaseAmount: p.shoppingVoucher?.autoRepurchaseAmount,
    autoRepurchaseValidityDays: p.shoppingVoucher?.autoRepurchaseValidityDays,
    referralPlanName: p.referralPlanName,
    region: p.region || "India",
    tags: p.tags || [],
    otherShoppingVouchers: res.otherShoppingVouchers || [],
  };
}
